import React from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, ThemeOptions } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const baseTheme: ThemeOptions = {
  palette: {
    primary: {
      main: '#ab8633', // Bronze
      light: '#D4AF37', // Metallic gold
      dark: '#8B4513', // Saddle brown
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#B87333', // Copper
      light: '#DAA520', // Goldenrod
      dark: '#8B6914', // Dark bronze
      contrastText: '#000000',
    },
    background: {
      default: '#fbfaf8', // Beige
      paper: '#fbfaf8', // Beige
    },
    text: {
      primary: '#2C1810', // Dark brown
      secondary: '#8B4513', // Saddle brown
    },
  },
  typography: {
    fontFamily: '"Playpen Sans Hebrew", "Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Playpen Sans Hebrew", serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: '"Playpen Sans Hebrew", serif',
      fontWeight: 600,
    },
    h3: {
      fontFamily: '"Playpen Sans Hebrew", serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Playpen Sans Hebrew", serif',
      fontWeight: 500,
      letterSpacing: '0.8px',
    },
    h5: {
      fontFamily: '"Playpen Sans Hebrew", serif',
      fontWeight: 500,
      letterSpacing: '0.8px',
    },
    h6: {
      fontFamily: '"Playpen Sans Hebrew", serif',
      fontWeight: 500,
      letterSpacing: '0.8px',
    },
    body1: {
      letterSpacing: '0.8px',
    },
    body2: {
      letterSpacing: '0.8px',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
          width: '100%',
          margin: 0,
          padding: 0,
          letterSpacing: '0.8px',
        },
        '#root': {
          height: '100%',
          width: '100%',
        },
        '[dir="rtl"]': {
          '& p, & h1, & h2, & h3, & h4, & h5, & h6, & span, & div': {
            unicodeBidi: 'embed',
            direction: 'rtl',
            textAlign: 'right',
          },
          '& .punctuation': {
            unicodeBidi: 'bidi-override',
            direction: 'ltr',
            display: 'inline-block',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const direction = i18n.language === 'he' ? 'rtl' : 'ltr';

  const theme = createTheme({
    ...baseTheme,
    direction,
  });

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
}; 